import { X } from "lucide-react";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { useId, type ReactNode } from "react";
import clsx from "clsx";

import useStyles from "./index.styles";

export type ConfirmTone = "primary" | "danger";

export interface ConfirmPopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTone?: ConfirmTone;
  isLoading?: boolean;
  disableConfirm?: boolean;
  hideCloseButton?: boolean;
  hideCancel?: boolean;
}

const ConfirmPopup = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmTone = "primary",
  isLoading = false,
  disableConfirm = false,
  hideCloseButton = false,
  hideCancel = false,
}: ConfirmPopupProps) => {
  const { classes } = useStyles();
  const titleId = useId();
  const descriptionId = useId();
  const hasDescription = description != null && description !== "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={hasDescription ? descriptionId : undefined}
      className={classes.body}
    >
      <Box className={classes.modalContent}>
        {!hideCloseButton && (
          <IconButton
            className={classes.closeButton}
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close confirmation"
          >
            <X />
          </IconButton>
        )}

        <Box className={classes.contentBody}>
          <Typography id={titleId} className={classes.title}>
            {title}
          </Typography>

          {hasDescription && (
            <Typography
              id={descriptionId}
              className={classes.description}
              component="div"
            >
              {description}
            </Typography>
          )}

          {children}

          <Box className={classes.actions}>
            {!hideCancel && (
              <Button
                className={classes.cancelButton}
                onClick={onClose}
                disabled={isLoading}
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              className={clsx(
                classes.confirmButton,
                confirmTone === "danger"
                  ? classes.confirmDanger
                  : classes.confirmPrimary
              )}
              onClick={onConfirm}
              disabled={isLoading || disableConfirm}
            >
              {isLoading ? "Processing..." : confirmLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConfirmPopup;
